import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Box, Button, TextField, Typography, Container, CssBaseline, 
    Select, MenuItem, FormControl, InputLabel,
    InputAdornment, IconButton
} from '@mui/material';
import api from '../api/api';

import logo from '../assets/images/logo.png'; 
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const decodeJwt = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (e) {
        return null;
    }
};


    const intentarObtenerUsbBot = async () => {
    try {
        const res = await fetch('http://127.0.0.1:8765/token', { signal: AbortSignal.timeout(800) });
        if (!res.ok) return null;
        return await res.json(); // { hardware_id, usb_hardware_token }
    } catch {
        return null; // el bot no está corriendo en esta PC, sigue el flujo normal
    }
};


const getDeviceData = () => {
    const ua = navigator.userAgent;
    let os = "Desconocido";
    let model = navigator.platform;
    
    if (ua.indexOf("Win") !== -1) os = "Windows";
    if (ua.indexOf("Mac") !== -1) os = "MacOS";
    if (ua.indexOf("X11") !== -1) os = "UNIX";
    if (ua.indexOf("Linux") !== -1) os = "Linux";
    if (ua.indexOf("Android") !== -1) os = "Android";
    if (ua.indexOf("iPhone") !== -1) os = "iOS";

    return { os, model };
};

const getOrGenerateDeviceToken = () => {
    let token = localStorage.getItem('device_token');
    if (!token) {
        const array = new Uint32Array(4);
        window.crypto.getRandomValues(array);
        const randomHex = Array.from(array, dec => dec.toString(16).padStart(8, '0')).join('');
        token = `dev_${randomHex}_${Date.now()}`;
        localStorage.setItem('device_token', token);
    }
    return token;
};

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    // 🔑 Estados para la autenticación por USB
    const [hardwareId, setHardwareId] = useState('');
    const [usbHardwareToken, setUsbHardwareToken] = useState('');

    const [error, setError] = useState('');
    const [tempToken, setTempToken] = useState(null);
    const [step, setStep] = useState(1);
    const [sucursales, setSucursales] = useState([]);
    const [selectedSucursal, setSelectedSucursal] = useState('');
    const [welcomeMessage, setWelcomeMessage] = useState(null);
    const [showPassword, setShowPassword] = useState(false);

    const navigate = useNavigate();

    const getRedirectPath = (decoded) => {
        if (!decoded?.id_rol) return '/clientes';
        const rol = Number(decoded.id_rol);
        if (rol === 5) return '/ruta-programada';
        if (rol === 4) return '/camaras';
        return '/clientes';
    };

    const handleClickShowPassword = () => setShowPassword(!showPassword);
    const handleMouseDownPassword = (event) => event.preventDefault();



    const handleError = (err) => {
        if (err.response?.data?.detail) {
            const detail = err.response.data.detail;
            if (Array.isArray(detail)) {
                setError(detail[0]?.msg || 'Error al iniciar sesión');
            } else if (typeof detail === 'string') {
                setError(detail);
            } else {
                setError('Error al iniciar sesión');
            }
        } else {
            setError('Error al iniciar sesión');
        }
    };

    const handleLoginStep1 = async (e) => {
        e.preventDefault();
        setError('');
        localStorage.removeItem('token');

        try {
            const deviceToken = getOrGenerateDeviceToken();
            const deviceDetails = getDeviceData();

            // 🔑 Intenta consultar al bot USB justo en el momento de presionar Continuar
            const usbBot = await intentarObtenerUsbBot();

            const payload = {
                email,
                password,
                device_token: deviceToken,
                hardware_id: usbBot?.hardware_id || hardwareId || null,
                usb_hardware_token: usbBot?.usb_hardware_token || usbHardwareToken || null,
                device_os: deviceDetails.os,
                device_model: deviceDetails.model,
                device_type: "Navegador Web"
            };

            const response = await api.post('/usuarios/login-auth', payload);
            const { sucursales, token } = response.data;
            
            if (sucursales && sucursales.length > 0) {
                setSucursales(sucursales);
                setTempToken(token); 
                setSelectedSucursal(sucursales[0].id_sucursal); 
                setStep(2);
            } else {
                setError('No se encontraron sucursales asociadas a tu cuenta.');
            }
            
        } catch (err) {
            handleError(err);
        }
    };

    const handleLoginStep2 = async (e) => {
        e.preventDefault();
        setError('');

        if (!selectedSucursal) {
            setError('Por favor, selecciona una sucursal.');
            return;
        }
        if (!tempToken) {
            setError('Error de sesión temporal. Vuelve a iniciar sesión.');
            setStep(1);
            return;
        }

        try {
            localStorage.removeItem('token');

            const finalResponse = await api.post(`/usuarios/login-auth-sucursal`, 
                { sucursal_login: selectedSucursal },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${tempToken}`
                    }
                }
            );
            
            const { token } = finalResponse.data;
            localStorage.setItem('token', token); 
            setTempToken(null); 
            
            const decoded = decodeJwt(token);
            
            if (decoded) {
                const nombreUsuario = decoded.nombre_usuario || 'Usuario'; 
                const nombreSucursal = decoded.nombre_sucursal || 'la sucursal seleccionada';
                
                const welcomeTitle = `¡Bienvenido ${nombreUsuario}! a sucursal ${nombreSucursal}.`;
                const welcomeNote = `Recuerda que tu IP de ubicación y hora de acceso son monitoreados para auditoría. Ten un buen día.`;
                
                setWelcomeMessage({ title: welcomeTitle, note: welcomeNote });
                const redirectTo = getRedirectPath(decoded);

                setTimeout(() => {
                    navigate(redirectTo, { replace: true });
                }, 6000);

            } else {
                navigate('/clientes', { replace: true });
            }

        } catch (err) {
            console.error('Error en login step 2:', err);
            handleError(err);
        }
    };

    const LoginForm = (
        <Box 
            component="form" 
            onSubmit={step === 1 ? handleLoginStep1 : handleLoginStep2} 
            noValidate 
            sx={{ 
                mt: 3,
                p: 4,
                bgcolor: 'rgba(255, 255, 255, 0.92)',
                borderRadius: 3,
                boxShadow: '0 12px 40px rgba(0,0,0,0.14)',
                backdropFilter: 'blur(8px)',
                maxWidth: 420,
                width: '100%',
            }}
        >
            {step === 1 && (
                <>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Correo Electrónico"
                        name="email"
                        autoComplete="email"
                        autoFocus
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Contraseña"
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="cambiar visibilidad de contraseña"
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* 🔑 CAMPOS OPCIONALES: Autenticación por USB (Hardware ID + Token HMAC) */}
                    <TextField
                        margin="dense"
                        fullWidth
                        name="hardwareId"
                        label="Hardware ID (Opcional)"
                        id="hardwareId"
                        placeholder="Pega el Hardware ID generado por el script"
                        value={hardwareId}
                        onChange={(e) => setHardwareId(e.target.value)}
                        variant="standard"
                        sx={{ mt: 1 }}
                    />
                    <TextField
                        margin="dense"
                        fullWidth
                        name="usbHardwareToken"
                        label="Token USB / HMAC (Opcional)"
                        type="password"
                        id="usbHardwareToken"
                        placeholder="Pega el token HMAC generado por el script"
                        value={usbHardwareToken}
                        onChange={(e) => setUsbHardwareToken(e.target.value)}
                        variant="standard"
                        sx={{ mt: 1 }}
                    />
                </>
            )}

            {step === 2 && (
                <FormControl fullWidth margin="normal" required>
                    <InputLabel id="sucursal-select-label">Selecciona Sucursal</InputLabel>
                    <Select
                        labelId="sucursal-select-label"
                        id="sucursal-select"
                        value={selectedSucursal}
                        label="Selecciona Sucursal"
                        onChange={(e) => setSelectedSucursal(e.target.value)}
                    >
                        {sucursales.map((sucursal) => (
                            <MenuItem key={sucursal.id_sucursal} value={sucursal.id_sucursal}>
                                {sucursal.nombre_sucursal}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
            
            {error && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {error}
                </Typography>
            )}
            
            <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 4, mb: 2, py: 1.5, fontSize: '1.1rem' }}
            >
                {step === 1 ? 'Continuar' : 'Iniciar Sesión'}
            </Button>
        </Box>
    );

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(120deg, #ffffff 0%, #fafafa 100%)',
            }}
        >
            <Container component="main" maxWidth="xs">
                <CssBaseline />
                
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 1,
                        mt: -10,
                    }}
                >
                    {welcomeMessage ? (
                        <Box sx={{ 
                            p: 5, 
                            bgcolor: 'rgba(255,255,255,0.95)', 
                            borderRadius: 3, 
                            boxShadow: 6,
                            textAlign: 'center',
                            maxWidth: 500
                        }}>
                            <Typography 
                                component="h1" 
                                variant="h4" 
                                color="primary" 
                                fontWeight="bold" 
                                gutterBottom
                            >
                                {welcomeMessage.title}
                            </Typography>
                            
                            <Typography 
                                variant="body1" 
                                color="text.secondary" 
                                sx={{ mb: 3, lineHeight: 1.6 }}
                            >
                                {welcomeMessage.note}
                            </Typography>
                            
                            <Typography variant="body1" color="text.secondary">
                                Redirigiendo...
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ mb: 5, textAlign: 'center' }}>
                                <img 
                                    src={logo}
                                    alt="Logo Villa"
                                    style={{ 
                                        maxWidth: '220px',
                                        height: 'auto',
                                        display: 'block',
                                        margin: '0 auto -50px',
                                    }}
                                />
                            </Box>
                            {LoginForm}
                        </>
                    )}
                </Box>
            </Container>
        </Box>
    );
};

export default Login;